import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import {
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	Tooltip,
} from '@mui/material';
import {
	ExternalLink, Filter, List, RefreshCcw
} from 'lucide-react';
import BackdropLoader from '../../../Components/Loaders/BackdropLoader';
import { urls } from '../../../Constants';
import { filtersInfoChip } from '@/Helpers';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import GlobalSearch from '@/Components/globalSearch/search';
import drawerActions from '@/Actions/drawer.actions';
import { Button } from '@/Components/ui/button';
import kitIssuanceServices from '../../../Services/kit-issuance.services';

const DATE_FORMAT = 'YYYY-MM-DD';

// one tab per order jsonData.typ the API returns
const KIT_TABS = [
	{
		value: 'ingKit', label: 'Ingredient Issuance',
	},
	{
		value: 'transferIntermediate', label: 'SFG Issuance',
	},
];

// the same filter sidebar as FMS Orders List: kitchen is mandatory, then a scheduled date
// range or production plans (picking plans greys the dates out and they are not sent)
const FILTERS_INFO = {
	header: {
		store: false,
		date: false,
		statusAndTypes: false,
		process: false,
		product: false,
	},
	store: {
		text: 'Kitchen *',
		show: true,
		mandatory: true,
		noAllOption: true,
	},
	dateRangeWithType: {
		text: 'Scheduled Date',
		show: true,
	},
	productionPlan: {
		text: 'Production Plans',
		show: true,
	},
};

const TIME_TYPES = [
	{
		label: 'Scheduled At', value: 'scheduledAt',
	},
];

// search bar field picker (same GlobalSearch as FMS Orders List), filtering the loaded kits
const SEARCH_OPTIONS = [
	{
		label: 'Order ID', value: 'orderId', placeholder: 'Search by Order ID',
	},
	{
		label: 'Kit Name', value: 'kitName', placeholder: 'Search by Kit Name',
	},
	{
		label: 'Plan', value: 'productionPlanName', placeholder: 'Search by Production Plan',
	},
	{
		label: 'Process', value: 'process', placeholder: 'Search by Issuing Process / Issued To',
	},
];

const searchFields = (kit, field) => {
	if (field === 'process') return [kit?.fromProcessName, kit?.toProcessName];
	return [kit?.[field]];
};

const STATUSES = [
	{
		value: 'ALL', label: 'All Statuses',
	},
	{
		value: 'PENDING', label: 'Pending',
	},
	{
		value: 'COMPLETED', label: 'Completed',
	},
];

const formatDateTime = (val, format = 'DD-MMM-YYYY hh:mm a') => {
	if (!val) return '-';
	return moment(val, 'YYYY-MM-DD HH:mm:ss').format(format);
};

const formatQty = (qty, unit) => {
	if (qty === null || qty === undefined) return '-';
	const rounded = Math.round(Number(qty) * 1000) / 1000;
	return unit ? `${rounded} ${unit}` : `${rounded}`;
};

// isIssued comes from the API: consumed with a measured qty above 0. Same rule here when a backend
// that predates the flag does not send it, so such rows are not all shown as not issued.
const isItemIssued = (item) => {
	return item?.isIssued ?? (!!item?.isConsumed && Number(item?.measuredQty) > 0);
};

const itemStatus = (item) => {
	if (item?.issuedInOtherKit) return 'Issued in other kit';
	if (item?.skipMeasurement) return 'Skipped';
	if (isItemIssued(item)) return 'Issued';
	if (item?.isConsumed) return 'Not issued (0 qty)';
	return 'Pending';
};

const itemStatusColor = (item) => {
	if (item?.skipMeasurement || item?.issuedInOtherKit) return 'default';
	if (isItemIssued(item)) return 'success';
	return item?.isConsumed ? 'error' : 'warning';
};

// a kit's items - the Kit Items dialog and the hover card on the Completion column
const KitItemsTable = ({ items }) => {
	return (
		<Table size='small'>
			<TableHead>
				<TableRow>
					<TableCell>Item</TableCell>
					<TableCell align='right'>Required Qty</TableCell>
					<TableCell align='right'>Measured Qty</TableCell>
					<TableCell>Status</TableCell>
					<TableCell>Issued At</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{(items || []).length === 0 && (
					<TableRow>
						<TableCell colSpan={5} align='center'>
							No items in this kit.
						</TableCell>
					</TableRow>
				)}
				{(items || []).map((item) => {
					return (
						<TableRow key={item.id}>
							<TableCell>{item.name || '-'}</TableCell>
							<TableCell align='right'>{formatQty(item.qty, item.unit)}</TableCell>
							<TableCell align='right'>{formatQty(item.measuredQty, item.unit)}</TableCell>
							<TableCell>
								<Chip
									size='small'
									color={itemStatusColor(item)}
									label={itemStatus(item)}
								/>
							</TableCell>
							<TableCell>{formatDateTime(item.consumedAt)}</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
};

// MUI's dark tooltip styled as a white card wide enough for the item table
const ITEMS_TOOLTIP_PROPS = {
	tooltip: {
		sx: {
			bgcolor: 'common.white',
			color: 'text.primary',
			maxWidth: 'none',
			boxShadow: 4,
			border: '1px solid #e5e7eb',
			p: 1,
		},
	},
	arrow: {
		sx: {
			color: 'common.white',
		},
	},
};

const KitIssuance = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { pathname, search } = useLocation();
	const query = useMemo(() => {
		return new URLSearchParams(search);
	}, [search]);

	// not `tab` - ImagesFinder keeps its own filter state in the URL and owns that key
	const activeTab = KIT_TABS.find((tab) => { return tab.value === query.get('kitTab'); }) ?? KIT_TABS[0];

	const [kits, setKits] = useState([]);
	const [filterValue, setFilterValue] = useState({});
	const [isLoading, setLoading] = useState(false);
	// GlobalSearch's selection: { value: field, searchValue: text }
	const [searchBy, setSearchBy] = useState({});
	const [status, setStatus] = useState('ALL');
	const [itemsKit, setItemsKit] = useState(null);

	const openFilters = () => {
		dispatch(drawerActions.toggleFiltersDrawer(true));
	};

	// ImagesFinder's onSubmit; it also fires once on load (from the URL, or with defaults)
	const fetchKits = async (filters) => {
		setFilterValue(filters);
		const storeId = filters?.store?.id;
		if (!storeId) {
			// first visit: nothing picked yet, the drawer asks for the kitchen
			setKits([]);
			openFilters();
			return;
		}
		const planIds = filters?.productionPlan?.id ?? [];
		try {
			setLoading(true);
			const res = await kitIssuanceServices.list({
				data: planIds.length > 0 ? {
					storeId,
					productionPlanIds: planIds,
				} : {
					storeId,
					dateFrom: moment(filters?.dateRange?.from).format(DATE_FORMAT),
					dateTo: moment(filters?.dateRange?.to).format(DATE_FORMAT),
				},
			});
			setKits(res?.statusCode === 200 ? (res?.data || []) : []);
		} catch (err) {
			// handleResponse already raised the error notification
			// eslint-disable-next-line no-console
			console.error(err);
			setKits([]);
		} finally {
			setLoading(false);
		}
	};

	const handleTabChange = (_e, value) => {
		const params = new URLSearchParams(search);
		params.set('kitTab', value);
		history.replace({
			pathname,
			search: params.toString(),
		});
	};

	// kits per tab, before search / status, for the tab labels
	const tabCounts = useMemo(() => {
		return kits.reduce((acc, kit) => {
			acc[kit?.kitType] = (acc[kit?.kitType] || 0) + 1;
			return acc;
		}, {});
	}, [kits]);

	const filteredKits = useMemo(() => {
		const text = (searchBy?.searchValue ?? '').trim().toLowerCase();
		const field = searchBy?.value ?? SEARCH_OPTIONS[0].value;
		return kits.filter((kit) => {
			if (kit?.kitType !== activeTab.value) return false;
			if (status === 'COMPLETED' && !kit?.isCompleted) return false;
			if (status === 'PENDING' && kit?.isCompleted) return false;
			if (!text) return true;
			return searchFields(kit, field).some((val) => {
				return String(val ?? '').toLowerCase().includes(text);
			});
		});
	}, [kits, searchBy, activeTab, status]);

	const completedCount = useMemo(() => {
		return filteredKits.filter((kit) => {
			return kit?.isCompleted;
		}).length;
	}, [filteredKits]);

	// same page the Cooking History "View" button opens, in a new tab
	const handleView = (kit) => {
		const path = `${urls.EXTENSION}${urls.PAST_QC}/${kit?.productId ?? kit?.recipeId}?id=${kit?.recipeId}&type=CMS`;
		window.open(path, '_blank', 'noopener');
	};

	const emptyMessage = () => {
		if (isLoading) return 'Loading...';
		if (!filterValue?.store?.id) {
			return 'Select a kitchen and a date range or production plan from Filters.';
		}
		return `No ${activeTab.label.toLowerCase()} kits for the selected filters.`;
	};

	return (
		<div className='p-4'>
			<BackdropLoader isLoading={isLoading} showProgress />
			{/* header + search row laid out like FMS Orders List: search fills the row, refresh and the
			filter drawer button sit at the right end */}
			<div className='flex flex-col gap-2 justify-between w-full p-2'>
				<div className='flex justify-between items-center gap-2'>
					<span className='font-bold text-[16px]'>
						Kit Issuance
					</span>
				</div>
				<div className='flex gap-3 w-full justify-start items-center py-1'>
					<div className='w-[90%]'>
						<GlobalSearch
							dropDownOptions={SEARCH_OPTIONS}
							setSelectedValue={setSearchBy}
						/>
					</div>
					<Tooltip title='Refresh Kits'>
						<RefreshCcw
							className={`size-5 ${filterValue?.store?.id ? 'cursor-pointer' : 'opacity-40'}`}
							onClick={() => {
								if (filterValue?.store?.id) fetchKits(filterValue);
							}}
						/>
					</Tooltip>
					<Button
						onClick={openFilters}
						className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700'
					>
						<span className='text-nowrap'>Filters</span>
						<Filter className='size-4' />
					</Button>
				</div>
			</div>
			{filtersInfoChip(FILTERS_INFO, filterValue)}
			<ImagesFinder
				filtersInfo={FILTERS_INFO}
				onSubmit={fetchKits}
				timeTypeArray={TIME_TYPES}
				exportInfo={{
					hideExportAccordian: true,
				}}
			/>

			<div className='flex flex-wrap justify-between items-center gap-3 mb-2 border-b border-gray-200'>
				<Tabs
					value={activeTab.value}
					onChange={handleTabChange}
				>
					{KIT_TABS.map((tab) => {
						return (
							<Tab
								key={tab.value}
								value={tab.value}
								label={`${tab.label} (${tabCounts[tab.value] || 0})`}
							/>
						);
					})}
				</Tabs>
				<div className='flex items-center gap-3'>
					<FormControl size='small'>
						<Select
							value={status}
							onChange={(e) => { return setStatus(e.target.value); }}
						>
							{STATUSES.map((opt) => {
								return (
									<MenuItem key={opt.value} value={opt.value}>
										{opt.label}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
					<Chip size='small' color='success' label={`Completed: ${completedCount}`} />
					<Chip size='small' color='warning' label={`Pending: ${filteredKits.length - completedCount}`} />
					<span className='text-[16px]'>
						Total:
						{' '}
						<span className='font-semibold'>{filteredKits.length}</span>
					</span>
				</div>
			</div>

			<TableContainer component={Paper}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell>Order ID</TableCell>
							<TableCell>Kit Name</TableCell>
							<TableCell>Issued To</TableCell>
							<TableCell>Issuing Process</TableCell>
							<TableCell>Plan</TableCell>
							<TableCell>Scheduled Date</TableCell>
							<TableCell>Completed</TableCell>
							<TableCell sx={{
								minWidth: '10rem',
							}}
							>
								Completion
							</TableCell>
							<TableCell align='right'>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredKits.length === 0 && (
							<TableRow>
								<TableCell colSpan={9} align='center'>
									{emptyMessage()}
								</TableCell>
							</TableRow>
						)}
						{filteredKits.map((kit) => {
							return (
								<TableRow key={kit.id}>
									<TableCell>{kit.orderId}</TableCell>
									<TableCell>{kit.kitName || '-'}</TableCell>
									<TableCell>{kit.fromProcessName || '-'}</TableCell>
									<TableCell>{kit.toProcessName || '-'}</TableCell>
									<TableCell>
										{kit.productionPlanName || '-'}
										<div className='text-xs text-gray-500'>
											{`#${kit.productionPlanId}`}
										</div>
									</TableCell>
									<TableCell>{formatDateTime(kit.scheduledAt)}</TableCell>
									<TableCell>
										<Chip
											size='small'
											color={kit.isCompleted ? 'success' : 'warning'}
											label={kit.isCompleted ? 'Yes' : 'No'}
										/>
									</TableCell>
									<TableCell>
										<Tooltip
											arrow
											placement='left'
											enterDelay={200}
											componentsProps={ITEMS_TOOLTIP_PROPS}
											title={(
												<div className='min-w-[36rem]'>
													<div className='px-2 pt-1 pb-2 text-sm font-semibold'>
														{kit.kitName || 'Kit Items'}
													</div>
													<KitItemsTable items={kit.items} />
												</div>
											)}
										>
											<div className='cursor-default'>
												<div className='flex items-center gap-2'>
													<LinearProgress
														variant='determinate'
														value={Math.min(kit.completionPercentage || 0, 100)}
														color={kit.completionPercentage >= 100 ? 'success' : 'primary'}
														sx={{
															flex: 1,
															height: 6,
															borderRadius: 3,
														}}
													/>
													<span className='text-sm'>{`${kit.completionPercentage}%`}</span>
												</div>
												<div className='text-xs text-gray-500'>
													{`${kit.itemsIssued}/${kit.itemsTotal} items issued`}
												</div>
											</div>
										</Tooltip>
									</TableCell>
									<TableCell align='right'>
										<div className='flex justify-end'>
											<Tooltip title='Kit Items'>
												<IconButton onClick={() => { return setItemsKit(kit); }}>
													<List className='size-4' />
												</IconButton>
											</Tooltip>
											<Tooltip title='View (opens in new tab)'>
												<IconButton onClick={() => { return handleView(kit); }}>
													<ExternalLink className='size-4' />
												</IconButton>
											</Tooltip>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog
				open={!!itemsKit}
				onClose={() => { return setItemsKit(null); }}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle>
					{itemsKit?.kitName || 'Kit Items'}
					<div className='text-sm text-gray-600 font-normal'>
						{`Order ${itemsKit?.orderId ?? '-'} · Issuing Process: ${itemsKit?.toProcessName || '-'} · Issued To: ${itemsKit?.fromProcessName || '-'}`}
					</div>
				</DialogTitle>
				<DialogContent dividers>
					<KitItemsTable items={itemsKit?.items} />
				</DialogContent>
				<DialogActions>
					<Button variant='outline' onClick={() => { return setItemsKit(null); }}>Close</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default KitIssuance;
